import React from 'react';
import { Box, Typography, Paper } from '@material-ui/core';
import { ReportCardComponentProps } from '../schema-types';
import { filterDOMProps } from '../utils/prop-normalizer';
import { PaletteColor } from '../../../../lib/types';

interface GrowthSkill {
  name: string;
  description: string;
  growthPotential: 'high' | 'medium' | 'low';
  currentLevel?: number;
  maxLevel?: number;
}

interface GrowthAnalysisData {
  title?: string;
  subtitle?: string;
  skills?: GrowthSkill[];
}

interface GrowthAnalysisProps extends ReportCardComponentProps {
  data: GrowthAnalysisData;
  title?: string;
  subtitle?: string;
  skills?: GrowthSkill[];
}

const growthColors = {
  high: { bg: '#e8f5e8', border: '#4caf50', text: '#2e7d32' },
  medium: { bg: '#fff3e0', border: '#ff9800', text: '#e65100' },
  low: { bg: '#ffebee', border: '#f44336', text: '#c62828' }
};

const growthIcons = {
  high: '🌱',
  medium: '📈',
  low: '📊'
};

export const GrowthAnalysis: React.FC<GrowthAnalysisProps> = ({
  title = "Where can you Grow the Most?",
  subtitle,
  skills = [],
  data,
  baseData: _baseData,
  player: _player,
  playerAssessment: _playerAssessment,
  reportCardLevel: _reportCardLevel,
  context: _context,
  ...props
}) => {
  // Use data props if provided, otherwise use direct props
  const finalTitle = data?.title || title;
  const finalSubtitle = data?.subtitle || subtitle;
  const finalSkills = data?.skills || skills;

  // Ensure skills is always an array
  const safeSkills = Array.isArray(finalSkills) ? finalSkills : [];

  if (safeSkills.length === 0) {
    return (
      <Paper elevation={2} style={{ padding: 16, backgroundColor: '#f9f9f9' }}>
        <Typography variant="body2" color="textSecondary">
          No growth analysis data available
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={2} style={{ padding: 24, backgroundColor: '#fff' }} {...filterDOMProps(props)}>
      <Box display="flex" alignItems="center" mb={2}>
        <Box
          width={32}
          height={32}
          bgcolor={PaletteColor.BLUE.hex}
          borderRadius="50%"
          display="flex"
          alignItems="center"
          justifyContent="center"
          mr={2}
        >
          <Typography variant="body2" style={{ color: 'white', fontWeight: 'bold' }}>
            ?
          </Typography>
        </Box>
        <Typography variant="h6" style={{ fontWeight: 'bold', color: '#333' }}>
          {finalTitle}
        </Typography>
      </Box>

      {finalSubtitle && (
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {finalSubtitle}
        </Typography>
      )}

      <Box>
        {safeSkills.map((skill, index) => {
          const colors = growthColors[skill.growthPotential] || growthColors.medium;

          return (
            <Box
              key={index}
              p={2}
              mb={2}
              style={{
                backgroundColor: colors.bg,
                border: `2px solid ${colors.border}`,
                borderRadius: 8,
                display: 'flex',
                alignItems: 'flex-start'
              }}
            >
              <Box mr={2} mt={0.5}>
                <Typography variant="h6" style={{ fontSize: '1.2em' }}>
                  {growthIcons[skill.growthPotential]}
                </Typography>
              </Box>

              <Box flex={1}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2" style={{ fontWeight: 'bold', color: colors.text }}>
                    {skill.name}
                  </Typography>
                  {skill.currentLevel && skill.maxLevel && (
                    <Typography variant="caption" style={{ fontWeight: 'bold', color: colors.text }}>
                      {skill.currentLevel}/{skill.maxLevel}
                    </Typography>
                  )}
                </Box>

                <Typography variant="body2" style={{ color: colors.text, lineHeight: 1.4 }}>
                  {skill.description}
                </Typography>

                {skill.currentLevel && skill.maxLevel && (
                  <Box mt={1}>
                    <Box
                      width="100%"
                      height={8}
                      bgcolor="#e0e0e0"
                      borderRadius={4}
                      overflow="hidden"
                    >
                      <Box
                        height="100%"
                        bgcolor={colors.border}
                        width={`${(skill.currentLevel / skill.maxLevel) * 100}%`}
                        borderRadius={4}
                      />
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
};
